// react and js
import PropTypes from "prop-types";
import { React } from "react";
import { useTranslation } from "react-i18next";

// mui
import { Box, Typography, Stack, Link } from "@mui/material";

// components
import Iconify from "./Iconify";

// hooks

// layouts

// pages

// theme

// utils

// widgets

// ----------------------------------------------------------------------
// Code
// ----------------------------------------------------------------------
function numberWithCommas(x) {
  return parseFloat(x).toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function PlanItemFeature({ feature }) {
  PlanItemFeature.propTypes = {
    feature: PropTypes.any.isRequired,
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "start",
        justifyContent: "start",
      }}
    >
      <Iconify
        color="#61CE70"
        icon="eva:checkmark-outline"
        minWidth={25}
        minHeight={25}
        sx={{ mr: 1, mt: "1px" }}
      />
      {feature}
    </Box>
  );
}

function NewPlanItemApplications({
  planInformation,
  referenceTradingCurrency,
  sponsored,
  reviseAction,
  isStopPossible,
}) {
  NewPlanItemApplications.propTypes = {
    planInformation: PropTypes.any.isRequired,
    referenceTradingCurrency: PropTypes.any.isRequired,
    sponsored: PropTypes.any.isRequired,
    reviseAction: PropTypes.any.isRequired,
    isStopPossible: PropTypes.any.isRequired,
  };

  const { t } = useTranslation();

  const parts = planInformation.plan_fixed_price_value.split('.');
  const priceBeforeDot = parts[0];
  const priceAfterDot = parts[1] || '00';

  let paymentTenure = "";
  if (planInformation.plan_interval_unit === "DAY") {
    paymentTenure = t("components.newPlanItemApplications.tenure.daily");
  } else if (planInformation.plan_interval_unit === "WEEK") {
    paymentTenure = t("components.newPlanItemApplications.tenure.weekly");
  } else if (planInformation.plan_interval_unit === "MONTH") {
    paymentTenure = t("components.newPlanItemApplications.tenure.monthly");
  } else if (planInformation.plan_interval_unit === "YEAR") {
    paymentTenure = t("components.newPlanItemApplications.tenure.yearly");
  }

  let paymentCurrency = planInformation.plan_fixed_price_currency_code;
  if (planInformation.plan_fixed_price_currency_code === "EUR") {
    paymentCurrency = "€";
  }

  let tradingCurrency = referenceTradingCurrency;
  if (referenceTradingCurrency === "EUR") {
    tradingCurrency = "€";
  }

  const minApplicationEquity = (
    <Typography variant="contentMiddle">
      <Typography variant="contentMiddleBold">
        {t("components.newPlanItemApplications.equity.min", {
          amount: numberWithCommas(Number(planInformation.distribution_min_application_equity)),
          currency: tradingCurrency,
        })}{" "}
        {reviseAction === "downgrade" && sponsored === false  ? <sup>1</sup> : null}
        {reviseAction === "upgrade" && sponsored === false  ? <sup>2</sup> : null}
        {" "}
      </Typography>
      {t("components.newPlanItemApplications.perTradingAccount")}
    </Typography>
  );

  const optApplicationEquity = (
    <Typography variant="contentMiddle">
      <Typography variant="contentMiddleBold">
        {t("components.newPlanItemApplications.equity.opt", {
          amount: numberWithCommas(Number(planInformation.distribution_opt_application_equity)),
          currency: tradingCurrency,
        })}{" "}
        {reviseAction === "downgrade" && sponsored === false  ? <sup>1</sup> : null}
        {reviseAction === "upgrade" && sponsored === false  ? <sup>2</sup> : null}
        {" "}
      </Typography>
      {t("components.newPlanItemApplications.perTradingAccount")}
    </Typography>
  );

  const maxApplicationEquity = (
    <Typography variant="contentMiddle">
      <Typography variant="contentMiddleBold">
        {t("components.newPlanItemApplications.equity.max", {
          amount: numberWithCommas(Number(planInformation.distribution_max_application_equity)),
          currency: tradingCurrency,
        })}{" "}
        {reviseAction === "downgrade" && sponsored === false  ? <sup>1</sup> : null}
        {reviseAction === "upgrade" && sponsored === false  ? <sup>2</sup> : null}
        {" "}
      </Typography>
      {t("components.newPlanItemApplications.perTradingAccount")}
    </Typography>
  );

  const minApplicationLeverage = (
    <Typography variant="contentMiddle">
      <Typography variant="contentMiddleBold">
        {t("components.newPlanItemApplications.leverage.min", {
          amount: numberWithCommas(Number(planInformation.distribution_min_application_leverage)),
        })}
      </Typography>
      {t("components.newPlanItemApplications.perTradingAccount")}
    </Typography>
  );

  const maxApplicationLeverage = (
    <Typography variant="contentMiddle">
      <Typography variant="contentMiddleBold">
        {t("components.newPlanItemApplications.leverage.max", {
          amount: numberWithCommas(Number(planInformation.distribution_max_application_leverage)),
        })}
      </Typography>
      {t("components.newPlanItemApplications.perTradingAccount")}
    </Typography>
  );

  return (
    <Stack>
      <Box
        sx={{
          borderRadius: "10px",
          boxShadow: "0px 0px 5px 0px rgba(0,0,0,0.6);",
          backgroundColor: "#C8C8C8",
        }}
      >
        <Box
          sx={{ pb: "0px", px: 2, mb: 1 }}
          display="block"
          align="center"
        >
          <Typography
            variant="h5"
            display="block"
            align="center"
            sx={{ mt: "14px" }}
          >
            <Link
              variant="h5"
              underline="none"
              color="black"
              fontWeight={300}
              target="_blank"
              href={planInformation.home_url}
            >
              {planInformation.product_name}
            </Link>
          </Typography>

          <Typography color="black" fontWeight={400} variant="contentHuge">
            {planInformation.plan_description}
          </Typography>
        </Box>

        <Box sx={{ px: 2, pb: 2, backgroundColor: "#FFFFFF", borderRadius: "0px 0px 10px 10px", }}>
          {sponsored === false ? (
            <>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  pt: 3,
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    lineHeight: 1,
                    display: "flex",
                    alignItems: "end",
                    mb: "4px",
                  }}
                >
                  {paymentCurrency}
                </Typography>

                <Typography variant="h1" sx={{ lineHeight: 1 }}>
                  {priceBeforeDot}
                </Typography>

                <Typography variant="h3" sx={{ lineHeight: 1, mt: "7px" }}>
                  {priceAfterDot}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  pb: 3,
                }}
              >
                <Typography variant="contentMiddle" sx={{ lineHeight: 1 }}>
                  {paymentTenure}{" "}{reviseAction === "upgrade" && isStopPossible === false ? <sup>1</sup> : null}
                  {reviseAction === "reactivate" && isStopPossible === false ? <sup>*</sup> : null}
                </Typography>
              </Box>
            </>
          ) : (
            <Box sx={{ pt: 3 }} />
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Box>
              {planInformation.distribution_min_application_equity !== null &&
              planInformation.distribution_min_application_equity !==
                undefined &&
              !Number.isNaN(
                Number(planInformation.distribution_min_application_equity)
              ) ? (
                <PlanItemFeature feature={minApplicationEquity} />
              ) : null}
              {planInformation.distribution_opt_application_equity !== null &&
              planInformation.distribution_opt_application_equity !==
                undefined &&
              planInformation.distribution_opt_application_equity !== "0" &&
              !Number.isNaN(
                Number(planInformation.distribution_opt_application_equity)
              ) ? (
                <PlanItemFeature feature={optApplicationEquity} />
              ) : null}
              {planInformation.distribution_max_application_equity !== null &&
              planInformation.distribution_max_application_equity !==
                undefined &&
              !Number.isNaN(
                Number(planInformation.distribution_max_application_equity)
              ) ? (
                <PlanItemFeature feature={maxApplicationEquity} />
              ) : null}
              {planInformation.distribution_min_application_leverage !== null &&
              planInformation.distribution_min_application_leverage !==
                undefined &&
              !Number.isNaN(
                Number(planInformation.distribution_min_application_leverage)
              ) ? (
                <PlanItemFeature feature={minApplicationLeverage} />
              ) : null}
              {planInformation.distribution_max_application_leverage !== null &&
              planInformation.distribution_max_application_leverage !==
                undefined &&
              !Number.isNaN(
                Number(planInformation.distribution_max_application_leverage)
              ) ? (
                <PlanItemFeature feature={maxApplicationLeverage} />
              ) : null}
            </Box>
          </Box>

        </Box>
      </Box>
    </Stack>
  );
}

export default NewPlanItemApplications;
